import React, { useRef, useEffect } from 'react';

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  height?: string;
  readOnly?: boolean;
}

const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  placeholder = 'Enter content...',
  height = '200px',
  readOnly = false
}) => {
  const editorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (editorRef.current) {
      editorRef.current.innerHTML = value;
      // Always set LTR direction for English text
      editorRef.current.style.direction = 'ltr';
      editorRef.current.style.textAlign = 'left';
    }
  }, [value]);

  const handleInput = (e: React.FormEvent<HTMLDivElement>) => {
    if (editorRef.current) {
      // Ensure LTR direction is maintained
      editorRef.current.style.direction = 'ltr';
      editorRef.current.style.textAlign = 'left';
      onChange(editorRef.current.innerHTML);
    }
  };

  const handleFocus = () => {
    if (editorRef.current) {
      // Ensure LTR direction when focusing
      editorRef.current.style.direction = 'ltr';
      editorRef.current.style.textAlign = 'left';
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    // Handle keyboard shortcuts
    if (e.ctrlKey || e.metaKey) {
      switch (e.key) {
        case 'b':
          e.preventDefault();
          document.execCommand('bold');
          break;
        case 'i':
          e.preventDefault();
          document.execCommand('italic');
          break;
        case 'u':
          e.preventDefault();
          document.execCommand('underline');
          break;
      }
    }
  };

  const execCommand = (command: string, value?: string) => {
    if (command === 'styleWithCSS' && value) {
      // Handle RTL/LTR direction
      if (value.includes('direction: rtl')) {
        document.execCommand('styleWithCSS', false, 'direction: rtl');
      } else if (value.includes('direction: ltr')) {
        document.execCommand('styleWithCSS', false, 'direction: ltr');
      }
    } else {
      document.execCommand(command, false, value);
    }
    if (editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
  };

  const setTextDirection = (direction: 'ltr' | 'rtl') => {
    if (editorRef.current) {
      editorRef.current.style.direction = direction;
      editorRef.current.style.textAlign = direction === 'rtl' ? 'right' : 'left';
    }
  };

  return (
    <div className="rich-text-editor" style={{ height: height }}>
      <div className="rich-text-toolbar">
        <button type="button" onClick={() => execCommand('bold')} title="Bold">
          <i className="fas fa-bold"></i>
        </button>
        <button type="button" onClick={() => execCommand('italic')} title="Italic">
          <i className="fas fa-italic"></i>
        </button>
        <button type="button" onClick={() => execCommand('underline')} title="Underline">
          <i className="fas fa-underline"></i>
        </button>
        <div className="toolbar-separator"></div>
        <button type="button" onClick={() => execCommand('insertUnorderedList')} title="Bullet List">
          <i className="fas fa-list-ul"></i>
        </button>
        <button type="button" onClick={() => execCommand('insertOrderedList')} title="Numbered List">
          <i className="fas fa-list-ol"></i>
        </button>
        <div className="toolbar-separator"></div>
        <button type="button" onClick={() => execCommand('justifyLeft')} title="Align Left">
          <i className="fas fa-align-left"></i>
        </button>
        <button type="button" onClick={() => execCommand('justifyCenter')} title="Align Center">
          <i className="fas fa-align-center"></i>
        </button>
        <button type="button" onClick={() => execCommand('justifyRight')} title="Align Right">
          <i className="fas fa-align-right"></i>
        </button>
        <div className="toolbar-separator"></div>
        <button type="button" onClick={() => execCommand('createLink')} title="Insert Link">
          <i className="fas fa-link"></i>
        </button>
        <button type="button" onClick={() => execCommand('removeFormat')} title="Clear Formatting">
          <i className="fas fa-remove-format"></i>
        </button>
        <div className="toolbar-separator"></div>
        <button type="button" onClick={() => setTextDirection('rtl')} title="Right to Left (RTL)">
          <i className="fas fa-paragraph"></i>
        </button>
        <button type="button" onClick={() => setTextDirection('ltr')} title="Left to Right (LTR)">
          <i className="fas fa-paragraph"></i>
        </button>
      </div>
      <div
        ref={editorRef}
        className="rich-text-content"
        contentEditable={!readOnly}
        onInput={handleInput}
        onKeyDown={handleKeyDown}
        onFocus={handleFocus}
        style={{ height: `calc(${height} - 42px)` }}
        data-placeholder={placeholder}
        suppressContentEditableWarning={true}
      />
    </div>
  );
};

export default RichTextEditor;
